import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Shield, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import Logo from '../components/Logo';
import { ProductPrintView } from '../features/thermal-insulations/ProductPrintView';
import { AirBarrierPrintView } from '../features/air-barriers/AirBarrierPrintView';
import { WeatherBarrierPrintViewInline } from '../features/weather-barriers/WeatherBarriersSection';
import { RoofingPrintView } from '../features/roofing/RoofingPrintView';
import { FlashingPrintView } from '../features/flashing-sheet-metal/FlashingPrintView';
import { JointProtectionPrintViewInline } from '../features/joint-protection/JointProtectionPrintView';

const API = process.env.REACT_APP_BACKEND_URL || '';

function getProductImage(product) {
  return product.imageCloudinary?.urls?.medium || product.imageUrl || null;
}

export default function PublicReviewPage() {
  const { entityType, qrToken } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/public/review/${entityType}/${qrToken}`);
        setProduct(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Product not found' : 'Failed to load product');
      } finally { setLoading(false); }
    };
    load();
  }, [entityType, qrToken]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-white border-b px-4 sm:px-6 py-3">
        <Logo size="sm" />
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-700">{error}</h2>
          <p className="text-sm text-slate-400 mt-1">The QR code may be invalid or the product has been removed.</p>
        </div>
      </div>
    </div>
  );

  const typeLabel = entityType === 'thermal-insulation' ? 'Thermal Insulation' : entityType === 'air-barrier' ? 'Air Barrier' : entityType === 'roofing' ? 'Roofing & Flashing' : entityType === 'flashing' ? 'Flashing & Sheet Metal' : entityType === 'joint-protection' ? 'Joint Protection' : 'Weather Barrier';
  const projectData = { name: product.projectName, number: product.projectNumber };
  const imageUrl = getProductImage(product);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-review-page">
      {/* Brand Header */}
      <div className="bg-white border-b px-4 sm:px-6 py-3 flex items-center justify-between" data-testid="public-brand-header">
        <div className="flex items-center gap-2">
          <Logo size="sm" showText={false} />
          <span className="text-sm font-semibold text-slate-700">Specard Inc.</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()} disabled={product.status !== 'active'} title={product.status !== 'active' ? 'Only active products can be printed' : 'Print'} data-testid="public-print-btn">
            <Printer className="h-4 w-4 mr-1.5" />Print
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto py-4 sm:py-6 px-4 sm:px-6">
        {/* Title + Product Info */}
        <div className="mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800" data-testid="public-page-title">Review</h1>
          <p className="text-sm text-slate-500 mt-1">
            <span className="font-medium text-slate-700" data-testid="public-product-name">{product.productNameModel}</span>
            {' · '}{typeLabel} · {product.manufacturerName || '—'}
            {product.projectName ? ` · ${product.projectName}` : ''}
          </p>
        </div>

        {/* Product Image */}
        {imageUrl && (
          <div className="mb-4" data-testid="public-product-image-container">
            <img
              src={imageUrl}
              alt={product.productNameModel}
              className="w-full max-h-64 sm:max-h-80 object-contain rounded-lg border bg-white"
              data-testid="public-product-image"
            />
          </div>
        )}

        {/* PrintView Content */}
        <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 overflow-hidden">
          {entityType === 'thermal-insulation' && (
            <ProductPrintView product={{ ...product, manufacturerName: product.manufacturerName }} project={projectData} />
          )}
          {entityType === 'air-barrier' && (
            <AirBarrierPrintView product={{ ...product, manufacturerName: product.manufacturerName }} project={projectData} />
          )}
          {entityType === 'weather-barrier' && (
            <WeatherBarrierPrintViewInline product={product} project={projectData} />
          )}
          {entityType === 'roofing' && (
            <RoofingPrintView product={{ ...product, manufacturerName: product.manufacturerName }} project={projectData} />
          )}
          {entityType === 'flashing' && (
            <FlashingPrintView product={{ ...product, manufacturerName: product.manufacturerName }} project={projectData} />
          )}
          {entityType === 'joint-protection' && (
            <JointProtectionPrintViewInline product={{ ...product, manufacturerName: product.manufacturerName }} project={projectData} />
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">This is a read-only review generated by Specard</p>
      </div>
    </div>
  );
}
